import Quill, { Delta, type Range } from "quill";
import "quill/dist/quill.core.css";
import { type Ref, useEffect, useRef, useState } from "react";
import {
  autolinkPastedText,
  cleanInlineText,
  type MrkdwnMode,
  type MrkdwnTreeNode,
  normalizeBroadcastMrkdwn,
  renderMrkdwn,
  serializeMrkdwn,
  stripInlineFormatting,
} from "../../../shared/mrkdwn";
import { slackUserIdFromQuery } from "../../../shared/slack-user-id";
import { matchSmartSymbol } from "../../../shared/smart-symbols";
import type { PingKind } from "../../../shared/types";
import { emojiUrlResolver, useEmojiCatalog } from "../../emoji";
import { standardEmojiByName } from "../../standard-emoji";
import type { FormatAction } from "./editor-types";

export type SlackEditorRange = { index: number; length: number };

type MentionValue = {
  kind: "user" | "channel" | "group";
  id: string;
  label: string;
};

export type EmojiValue = { name: string; url?: string; unicode?: string };
type BroadcastValue = { kind: "channel" | "here" };

const InlineEmbed = Quill.import("blots/embed") as typeof import("parchment").EmbedBlot;

class SlackMentionBlot extends InlineEmbed {
  static blotName = "slackMention";
  static tagName = "SPAN";
  static className = "ql-slack-mention";

  static create(raw: unknown) {
    const value = raw as MentionValue;
    // biome-ignore lint/complexity/noThisInStatic: Parchment uses the subclass as `this` to apply its blot class.
    const node = super.create() as HTMLElement;
    node.classList.add("mention");
    node.classList.toggle("channel", value.kind === "channel");
    node.classList.toggle("group", value.kind === "group");
    const attribute =
      value.kind === "user" ? "data-user-id" : value.kind === "channel" ? "data-channel-id" : "data-group-id";
    node.setAttribute(attribute, value.id);
    node.setAttribute("aria-label", `${value.kind} mention ${value.label.replace(/^[@#]/, "")}`);
    node.textContent = value.label;
    return node;
  }

  static value(node: HTMLElement): MentionValue {
    const user = node.getAttribute("data-user-id");
    const channel = node.getAttribute("data-channel-id");
    const group = node.getAttribute("data-group-id");
    return {
      kind: user ? "user" : channel ? "channel" : "group",
      id: user ?? channel ?? group ?? "",
      label: cleanInlineText(node.textContent ?? "").trim(),
    };
  }
}

class SlackEmojiBlot extends InlineEmbed {
  static blotName = "slackEmoji";
  static tagName = "SPAN";
  static className = "ql-slack-emoji";

  static create(raw: unknown) {
    const value = raw as EmojiValue;
    // biome-ignore lint/complexity/noThisInStatic: Parchment uses the subclass as `this` to apply its blot class.
    const node = super.create() as HTMLElement;
    node.setAttribute("data-emoji-name", value.name);
    node.setAttribute("aria-label", `:${value.name}:`);
    if (value.url) {
      const image = document.createElement("img");
      image.className = "inline-custom-emoji";
      image.src = value.url;
      image.alt = `:${value.name}:`;
      node.append(image);
    } else {
      node.setAttribute("data-emoji-unicode", value.unicode ?? "");
      const glyph = document.createElement("span");
      glyph.className = "inline-emoji-glyph";
      glyph.textContent = value.unicode ?? `:${value.name}:`;
      node.append(glyph);
    }
    return node;
  }

  static value(node: HTMLElement): EmojiValue {
    return {
      name: node.getAttribute("data-emoji-name") ?? "",
      url: node.querySelector("img")?.getAttribute("src") ?? undefined,
      unicode: node.getAttribute("data-emoji-unicode") ?? undefined,
    };
  }
}

class SlackBroadcastBlot extends InlineEmbed {
  static blotName = "slackBroadcast";
  static tagName = "SPAN";
  static className = "ql-slack-broadcast";

  static create(raw: unknown) {
    const value = raw as BroadcastValue;
    // biome-ignore lint/complexity/noThisInStatic: Parchment uses the subclass as `this` to apply its blot class.
    const node = super.create() as HTMLElement;
    node.classList.add("mention", "ping");
    node.setAttribute("data-ping", value.kind);
    node.setAttribute("aria-label", `notify ${value.kind}`);
    node.textContent = `@${value.kind}`;
    return node;
  }

  static value(node: HTMLElement): BroadcastValue {
    return { kind: node.getAttribute("data-ping") === "here" ? "here" : "channel" };
  }
}

if (!Quill.imports["formats/slackMention"]) {
  Quill.register(SlackMentionBlot);
  Quill.register(SlackEmojiBlot);
  Quill.register(SlackBroadcastBlot);
}

export const SLACK_EDITOR_FORMATS: Record<MrkdwnMode, string[]> = {
  rich: [
    "bold",
    "italic",
    "underline",
    "strike",
    "code",
    "link",
    "blockquote",
    "code-block",
    "list",
    "indent",
    "slackMention",
    "slackEmoji",
    "slackBroadcast",
  ],
  mrkdwn: [
    "bold",
    "italic",
    "strike",
    "code",
    "link",
    "blockquote",
    "code-block",
    "slackMention",
    "slackEmoji",
    "slackBroadcast",
  ],
  emoji: ["slackEmoji"],
};

function editorFormats(mode: MrkdwnMode, multiline: boolean) {
  if (multiline) return SLACK_EDITOR_FORMATS[mode];
  return SLACK_EDITOR_FORMATS[mode].filter(
    (format) => !["blockquote", "code-block", "list", "indent"].includes(format),
  );
}

function formatShortcut(event: KeyboardEvent, mode: MrkdwnMode, multiline: boolean): FormatAction | null {
  if (!(event.metaKey || event.ctrlKey) || mode === "emoji") return null;
  const key = event.key.toLowerCase();
  if (multiline && event.altKey && event.shiftKey && key === "c") return "codeBlock";
  if (event.altKey) return null;
  if (!event.shiftKey && key === "b") return "bold";
  if (!event.shiftKey && key === "i") return "italic";
  if (!event.shiftKey && key === "u" && mode === "rich") return "underline";
  if (event.shiftKey && key === "x") return "strike";
  if (event.shiftKey && key === "c") return "code";
  if (multiline && mode === "rich" && event.shiftKey && event.code === "Digit7") return "number";
  if (multiline && mode === "rich" && event.shiftKey && event.code === "Digit8") return "bullet";
  if (multiline && event.shiftKey && event.code === "Digit9") return "quote";
  return null;
}

function plainText(value: string) {
  return stripInlineFormatting(value)
    .replace(/<@[UW][A-Z0-9]+(?:\|([^>]+))?>/gi, (_match, label?: string) => label ?? "")
    .replace(/<#([CG][A-Z0-9]+)(?:\|([^>]+))?>/gi, (_match, _id: string, label?: string) => label ?? "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Adds the empty lines Quill needs to preserve boundaries between mrkdwn blocks. */
export function quillHtml(html: string, multiline: boolean) {
  if (!multiline) return html;
  const template = document.createElement("template");
  template.innerHTML = html;
  const blocks = Array.from(template.content.childNodes);
  for (const block of blocks.slice(0, -1)) {
    const spacer = document.createElement("p");
    spacer.append(document.createElement("br"));
    block.after(spacer);
  }
  return template.innerHTML;
}

function sameRange(a: SlackEditorRange | null, b: SlackEditorRange | null) {
  return a?.index === b?.index && a?.length === b?.length;
}

function setForwardedRef(ref: Ref<HTMLDivElement> | undefined, value: HTMLDivElement | null) {
  if (typeof ref === "function") ref(value);
  else if (ref) ref.current = value;
}

export type SlackEditorHandle = {
  root: HTMLDivElement;
  focus: () => void;
  getRange: () => SlackEditorRange | null;
  restoreRange: (range: SlackEditorRange | null) => void;
  getSelectedText: () => string;
  getTextBeforeCursor: () => string;
  getBounds: () => { left: number; top: number; right: number; bottom: number; width: number; height: number } | null;
  getFormats: () => Record<string, unknown>;
  format: (action: FormatAction) => void;
  insertText: (text: string) => void;
  replaceBeforeCursor: (length: number, text: string) => void;
  replaceBeforeCursorWithMention: (length: number, value: MentionValue) => void;
  replaceBeforeCursorWithEmoji: (length: number, value: EmojiValue) => void;
  insertMentionTrigger: (trigger: "@" | "#") => void;
  insertEmoji: (value: EmojiValue) => void;
  insertLink: (label: string, url: string, range?: SlackEditorRange | null) => void;
};

const editorHandles = new WeakMap<HTMLElement, SlackEditorHandle>();

export function slackEditorHandle(root: HTMLElement | null) {
  return root ? (editorHandles.get(root) ?? null) : null;
}

export function addSlackClipboardMatchers(quill: Quill) {
  quill.clipboard.addMatcher("[data-user-id]", (node) => {
    const element = node as HTMLElement;
    return new Delta().insert({
      slackMention: {
        kind: "user",
        id: element.getAttribute("data-user-id") ?? "",
        label: cleanInlineText(element.textContent ?? "").trim(),
      } satisfies MentionValue,
    });
  });
  quill.clipboard.addMatcher("[data-channel-id]", (node) => {
    const element = node as HTMLElement;
    return new Delta().insert({
      slackMention: {
        kind: "channel",
        id: element.getAttribute("data-channel-id") ?? "",
        label: cleanInlineText(element.textContent ?? "").trim(),
      } satisfies MentionValue,
    });
  });
  quill.clipboard.addMatcher("[data-group-id]", (node) => {
    const element = node as HTMLElement;
    return new Delta().insert({
      slackMention: {
        kind: "group",
        id: element.getAttribute("data-group-id") ?? "",
        label: cleanInlineText(element.textContent ?? "").trim(),
      } satisfies MentionValue,
    });
  });
  quill.clipboard.addMatcher("[data-ping]", (node) => {
    const kind = (node as HTMLElement).getAttribute("data-ping") === "here" ? "here" : "channel";
    return new Delta().insert({ slackBroadcast: { kind } satisfies BroadcastValue });
  });
  quill.clipboard.addMatcher("[data-emoji-name]", (node) => {
    const element = node as HTMLElement;
    const name = element.getAttribute("data-emoji-name") ?? "";
    const url = element.matches("img")
      ? (element.getAttribute("src") ?? "")
      : (element.querySelector("img")?.src ?? "");
    const unicode = url ? undefined : cleanInlineText(element.textContent ?? "").trim();
    return new Delta().insert({ slackEmoji: { name, url: url || undefined, unicode } satisfies EmojiValue });
  });
}

function toggleFormat(quill: Quill, action: FormatAction, range: SlackEditorRange) {
  if (action === "clear") {
    quill.removeFormat(range.index, range.length || 1, "user");
    return;
  }
  const format = quill.getFormat(range.index, range.length);
  const inline = action === "codeBlock" ? "code-block" : action;
  if (action === "bullet" || action === "number") {
    quill.formatLine(
      range.index,
      range.length || 1,
      "list",
      format.list === (action === "bullet" ? "bullet" : "ordered") ? false : action === "bullet" ? "bullet" : "ordered",
      "user",
    );
    return;
  }
  if (action === "quote" || action === "codeBlock") {
    quill.formatLine(
      range.index,
      range.length || 1,
      inline === "quote" ? "blockquote" : inline,
      format[inline === "quote" ? "blockquote" : inline] ? false : inline === "code-block" ? "plain" : true,
      "user",
    );
    return;
  }
  quill.formatText(range.index, range.length, inline, !format[inline], "user");
}

export type SlackEditorProps = {
  value: string;
  onChange: (value: string) => void;
  mode?: MrkdwnMode;
  multiline?: boolean;
  className?: string;
  id?: string;
  placeholder?: string;
  ariaLabel: string;
  maxLength?: number;
  ping?: PingKind;
  spellCheck?: boolean;
  submitOnEnter?: boolean;
  interceptEnter?: boolean;
  onSubmit?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  ref?: Ref<HTMLDivElement>;
};

export function SlackEditor({
  value,
  onChange,
  mode = "rich",
  multiline = true,
  className,
  id,
  placeholder,
  ariaLabel,
  maxLength,
  ping = "none",
  spellCheck = true,
  submitOnEnter = false,
  interceptEnter = false,
  onSubmit,
  onKeyDown,
  ref,
}: SlackEditorProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const { catalog } = useEmojiCatalog();
  const resolveEmojiUrl = emojiUrlResolver(catalog);
  const resolveEmoji = (name: string): EmojiValue | null => {
    const url = resolveEmojiUrl(name);
    if (url) return { name, url };
    const standard = standardEmojiByName(name);
    return standard ? { name: standard.name, unicode: standard.unicode } : null;
  };
  const quillRef = useRef<Quill | null>(null);
  const handleRef = useRef<SlackEditorHandle | null>(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const onSubmitRef = useRef(onSubmit);
  const optionsRef = useRef({ mode, multiline, maxLength, ping, submitOnEnter, interceptEnter });
  const renderRef = useRef<(value: string) => string>(() => "");
  const resolveEmojiRef = useRef<(name: string) => EmojiValue | null>(resolveEmoji);
  const renderedCatalogRef = useRef(catalog);
  const lastRangeRef = useRef<SlackEditorRange | null>(null);
  const [linkMenu, setLinkMenu] = useState<{
    left: number;
    top: number;
    url: string;
    anchor: HTMLAnchorElement;
  } | null>(null);
  const [pasteLimit, setPasteLimit] = useState(false);

  onChangeRef.current = onChange;
  onSubmitRef.current = onSubmit;
  optionsRef.current = { mode, multiline, maxLength, ping, submitOnEnter, interceptEnter };
  resolveEmojiRef.current = resolveEmoji;
  renderRef.current = (source) =>
    quillHtml(
      renderMrkdwn(source, {
        mode,
        multiline,
        ping,
        emojiUrl: resolveEmojiUrl,
        emojiUnicode: (name) => standardEmojiByName(name)?.unicode,
      }),
      multiline,
    );

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const quill = new Quill(host, {
      placeholder,
      formats: editorFormats(mode, multiline),
      modules: {
        toolbar: false,
        history: { delay: 800, maxStack: 100, userOnly: true },
      },
    });
    quillRef.current = quill;
    addSlackClipboardMatchers(quill);
    const root = quill.root;
    root.id = id ?? "";
    root.className = `ql-editor mrkdwn-editor ${multiline ? "multiline" : "single-line"}${className ? ` ${className}` : ""}`;
    root.setAttribute("role", "textbox");
    root.setAttribute("dir", "auto");
    root.setAttribute("aria-label", ariaLabel);
    root.setAttribute("aria-multiline", String(multiline));
    root.setAttribute("data-placeholder", placeholder ?? "");
    root.setAttribute("data-mrkdwn-editor", "true");
    root.setAttribute(
      "data-mrkdwn-mode",
      multiline ? mode : mode === "rich" ? "inline-rich" : mode === "mrkdwn" ? "inline-mrkdwn" : mode,
    );
    root.spellcheck = spellCheck;

    const setValue = (source: string, preserveSelection = false) => {
      const selection = preserveSelection ? (quill.getSelection() ?? lastRangeRef.current) : null;
      const delta = quill.clipboard.convert({ html: renderRef.current(source) });
      quill.setContents(delta, "silent");
      if (selection)
        quill.setSelection(Math.min(selection.index, Math.max(0, quill.getLength() - 1)), selection.length, "silent");
      root.classList.toggle("is-empty", !source.trim());
      root.dataset.atTop = "true";
      root.dataset.atBottom = String(root.scrollHeight <= root.clientHeight);
    };

    const commit = () => {
      const current = optionsRef.current;
      let next = serializeMrkdwn(root as unknown as MrkdwnTreeNode, { multiline: current.multiline });
      next = normalizeBroadcastMrkdwn(next, current.ping);
      if (current.maxLength !== undefined && next.length > current.maxLength) {
        next = next.slice(0, current.maxLength);
        setValue(next, true);
      }
      root.classList.toggle("is-empty", !next.trim());
      if (next === valueRef.current) return;
      valueRef.current = next;
      onChangeRef.current(next);
    };

    const currentRange = () => quill.getSelection() ?? lastRangeRef.current;
    const replace = (length: number, insert: string | Record<string, unknown>) => {
      const range = currentRange();
      if (!range) return;
      const start = Math.max(0, range.index - length);
      quill.deleteText(start, range.index - start + range.length, "user");
      if (typeof insert === "string") quill.insertText(start, insert, quill.getFormat(start, 0), "user");
      else {
        const [name, payload] = Object.entries(insert)[0] ?? [];
        if (name) quill.insertEmbed(start, name, payload, "user");
      }
      const end = start + (typeof insert === "string" ? insert.length : 1);
      quill.setSelection(end, 0, "silent");
      lastRangeRef.current = { index: end, length: 0 };
      commit();
    };

    const handle: SlackEditorHandle = {
      root,
      focus: () => quill.focus(),
      getRange: () => {
        const range = currentRange();
        return range ? { index: range.index, length: range.length } : null;
      },
      restoreRange: (range) => {
        if (!range) return;
        quill.focus();
        quill.setSelection(Math.min(range.index, Math.max(0, quill.getLength() - 1)), range.length, "silent");
      },
      getSelectedText: () => {
        const range = currentRange();
        return range ? quill.getText(range.index, range.length) : "";
      },
      getTextBeforeCursor: () => {
        const range = currentRange();
        return range ? quill.getText(0, range.index) : "";
      },
      getBounds: () => {
        const range = currentRange();
        if (!range) return null;
        const bounds = quill.getBounds(range.index, range.length);
        if (!bounds) return null;
        const editor = root.getBoundingClientRect();
        return {
          left: editor.left + bounds.left,
          top: editor.top + bounds.top,
          right: editor.left + bounds.right,
          bottom: editor.top + bounds.bottom,
          width: bounds.width,
          height: bounds.height,
        };
      },
      getFormats: () => {
        const range = currentRange();
        return range ? quill.getFormat(range.index, range.length) : {};
      },
      format: (action) => {
        const range = currentRange();
        if (!range) return;
        quill.focus();
        toggleFormat(quill, action, range);
        quill.setSelection(range.index, range.length, "silent");
        commit();
      },
      insertText: (text) => replace(0, text),
      replaceBeforeCursor: (length, text) => replace(length, text),
      replaceBeforeCursorWithMention: (length, mention) => replace(length, { slackMention: mention }),
      replaceBeforeCursorWithEmoji: (length, emoji) => replace(length, { slackEmoji: emoji }),
      insertMentionTrigger: (trigger) => replace(0, trigger),
      insertEmoji: (emoji) => replace(0, { slackEmoji: emoji }),
      insertLink: (label, url, savedRange) => {
        const range = savedRange ?? currentRange();
        if (!range) return;
        quill.focus();
        quill.deleteText(range.index, range.length, "user");
        quill.insertText(range.index, label, { link: url }, "user");
        quill.setSelection(range.index + label.length, 0, "silent");
        commit();
      },
    };
    handleRef.current = handle;
    editorHandles.set(root, handle);
    setForwardedRef(ref, root);
    setValue(valueRef.current);

    const updateScroll = () => {
      const scrollable = root.scrollHeight > root.clientHeight;
      root.dataset.atTop = String(!scrollable || root.scrollTop === 0);
      root.dataset.atBottom = String(!scrollable || root.scrollTop + root.clientHeight >= root.scrollHeight);
    };
    const onTextChange = (_delta: Delta, _previous: Delta, source: string) => {
      if (source !== "silent") commit();
      updateScroll();
    };
    const onSelectionChange = (range: Range | null) => {
      if (range && !sameRange(lastRangeRef.current, range))
        lastRangeRef.current = { index: range.index, length: range.length };
    };
    const onBeforeInput = (event: InputEvent) => {
      if (event.inputType !== "insertText" || !event.data) return;
      const range = currentRange();
      if (!range || range.length) return;
      const currentMode = optionsRef.current.mode;
      const before = quill.getText(0, range.index).replace(/\n/g, " ");
      if (event.data === ":") {
        const match = `${before}:`.match(/(?:^|\s):([a-z0-9_+-]{1,100}):$/i);
        const name = match?.[1]?.toLowerCase();
        const emoji = name ? resolveEmojiRef.current(name) : null;
        if (name && emoji) {
          event.preventDefault();
          replace(name.length + 1, { slackEmoji: emoji });
          return;
        }
      }
      if (currentMode !== "emoji" && event.data.length === 1 && /[)>=-]/.test(event.data)) {
        const symbol = matchSmartSymbol(before + event.data);
        const format = quill.getFormat(range.index, 0);
        if (symbol && !format.code && !format["code-block"]) {
          event.preventDefault();
          replace(symbol.pattern.length - 1, symbol.replacement);
        }
      }
    };
    const onNativeKeyDown = (event: KeyboardEvent) => {
      if (!optionsRef.current.multiline && event.key === "Enter") {
        event.preventDefault();
        return;
      }
      if (event.key === "Enter" && optionsRef.current.interceptEnter) {
        event.preventDefault();
        return;
      }
      if (
        event.key === "Enter" &&
        optionsRef.current.submitOnEnter &&
        !event.shiftKey &&
        !(event.metaKey || event.ctrlKey || event.altKey)
      ) {
        const range = currentRange();
        const format = range ? quill.getFormat(range.index, range.length) : {};
        if (!format.list && !format.blockquote && !format["code-block"]) {
          event.preventDefault();
          onSubmitRef.current?.();
          return;
        }
      }
      const action = formatShortcut(event, optionsRef.current.mode, optionsRef.current.multiline);
      if (!action) return;
      event.preventDefault();
      handle.format(action);
    };
    const onPaste = (event: ClipboardEvent) => {
      const raw = event.clipboardData?.getData("text/plain") ?? "";
      if (raw.length > 50_000) {
        event.preventDefault();
        setPasteLimit(true);
        return;
      }
      const range = currentRange();
      if (!range) return;
      if (/^https?:\/\/\S+$/i.test(raw.trim()) && range.length && optionsRef.current.mode !== "emoji") {
        event.preventDefault();
        const label = quill.getText(range.index, range.length);
        handle.insertLink(label, raw.trim(), range);
        return;
      }
      if (optionsRef.current.mode === "emoji") {
        event.preventDefault();
        replace(0, plainText(optionsRef.current.multiline ? raw : raw.replace(/\s*\n+\s*/g, " ")));
        return;
      }
      if (!event.clipboardData?.getData("text/html")) {
        event.preventDefault();
        let cleaned = optionsRef.current.multiline ? raw : raw.replace(/\s*\n+\s*/g, " ");
        cleaned = autolinkPastedText(cleaned);
        const userId = slackUserIdFromQuery(cleaned.trim());
        if (userId) cleaned = `<@${userId}> `;
        quill.deleteText(range.index, range.length, "user");
        quill.clipboard.dangerouslyPasteHTML(range.index, renderRef.current(cleaned), "user");
        quill.setSelection(Math.min(range.index + cleaned.length, quill.getLength() - 1), 0, "silent");
        commit();
      }
    };

    quill.on("text-change", onTextChange);
    quill.on("selection-change", onSelectionChange);
    root.addEventListener("scroll", updateScroll, { passive: true });
    root.addEventListener("beforeinput", onBeforeInput, true);
    root.addEventListener("keydown", onNativeKeyDown, true);
    root.addEventListener("paste", onPaste, true);

    return () => {
      quill.off("text-change", onTextChange);
      quill.off("selection-change", onSelectionChange);
      root.removeEventListener("scroll", updateScroll);
      root.removeEventListener("beforeinput", onBeforeInput, true);
      root.removeEventListener("keydown", onNativeKeyDown, true);
      root.removeEventListener("paste", onPaste, true);
      editorHandles.delete(root);
      handleRef.current = null;
      quillRef.current = null;
      setForwardedRef(ref, null);
      host.replaceChildren();
      host.classList.remove("ql-container");
    };
  }, [ariaLabel, className, id, mode, multiline, placeholder, ref, spellCheck]);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;
    const catalogChanged = renderedCatalogRef.current !== catalog;
    if (value === valueRef.current && !catalogChanged) return;
    const range = quill.getSelection() ?? lastRangeRef.current;
    valueRef.current = value;
    renderedCatalogRef.current = catalog;
    quill.setContents(quill.clipboard.convert({ html: renderRef.current(value) }), "silent");
    quill.root.classList.toggle("is-empty", !value.trim());
    if (range) quill.setSelection(Math.min(range.index, Math.max(0, quill.getLength() - 1)), range.length, "silent");
  }, [catalog, value]);

  return (
    <>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: Quill creates the semantic textbox inside this event boundary. */}
      <div
        ref={hostRef}
        className="slack-editor-shell"
        onKeyDown={(event) => onKeyDown?.(event)}
        onContextMenu={(event) => {
          const anchor = (event.target as HTMLElement).closest<HTMLAnchorElement>("a");
          if (!anchor) return;
          event.preventDefault();
          setLinkMenu({ left: event.clientX, top: event.clientY, url: anchor.href, anchor });
        }}
      />
      {linkMenu && (
        <>
          <button
            type="button"
            className="context-menu-backdrop"
            aria-label="Close link menu"
            onClick={() => setLinkMenu(null)}
            onContextMenu={(event) => event.preventDefault()}
          />
          <div className="context-menu" role="menu" style={{ left: linkMenu.left, top: linkMenu.top }}>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                void navigator.clipboard.writeText(linkMenu.url).catch(() => undefined);
                setLinkMenu(null);
              }}
            >
              Copy link
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                linkMenu.anchor.removeAttribute("href");
                quillRef.current?.update("user");
                setLinkMenu(null);
              }}
            >
              Remove link
            </button>
          </div>
        </>
      )}
      {pasteLimit && (
        <div className="paste-limit-notice" role="alert" onAnimationEnd={() => setPasteLimit(false)}>
          That&rsquo;s a bit too long to paste. There&rsquo;s a limit on the number of characters you can paste.
        </div>
      )}
    </>
  );
}
